import { Controller } from '@hotwired/stimulus'
import LiveWebcastAttendanceVerification from 'cle/live_webcast_attendance_verification'
import AJAX from 'utils/ajax'
import Modal from 'components/modal'
import { createClient } from 'cle/live_webcast_video_client'
import { hideElement, showElement } from 'utils/toggle_element_visibility'
import { loadTemplate } from "utils/load_template"
import tippy from 'tippy.js'

export default class extends Controller {

  static values = {
    videoName: String,
    checkpoints: Array,
    endDate: Number,
    attendanceTemplate: String,
    attendancePopupContainerSelector: String,
    attendancePopupCountDownContainerSelector: String,
    attendancePopupCountDownSeconds: Number,
    attendancePopupConnectionErrorContainerSelector: String,
    attendanceExpiredTemplate: String,
    toleranceSeconds: Number,
    attendanceUrl: String,
    attendanceAlreadyCompleted: Boolean,
    attendanceVerificationAudioUrl: String,
    meetingNumber: String,
    zoomClientId: String,
    authenticationEndpointUrl: String,
    userName: String,
    userEmail: String,
    containerId: String,
    viewsUrl: String
  }

  initialize(){
    try {
      this.setupAttendanceVerification()
      this.initializeLiveWebcast()
    } catch(e) {
      console.log(e)
      if(window.Rollbar) { window.Rollbar.error(e) }
      this.showFatalErrorModal(e)
      throw(e)
    }
  }

  setupAttendanceVerification(){
    this.attendanceVerification = new LiveWebcastAttendanceVerification({
      videoName: this.videoNameValue,
      checkpoints: this.checkpointsValue,
      attendanceTemplate: this.attendanceTemplateValue,
      attendancePopupContainerSelector: this.attendancePopupContainerSelectorValue,
      attendancePopupCountDownContainerSelector: this.attendancePopupCountDownContainerSelectorValue,
      attendancePopupCountDownSeconds: this.attendancePopupCountDownSecondsValue,
      attendancePopupConnectionErrorContainerSelector: this.attendancePopupConnectionErrorContainerSelectorValue,
      toleranceSeconds: this.toleranceSecondsValue,
      endDate: this.endDateValue,
      attendanceExpiredTemplate: this.attendanceExpiredTemplateValue,
      attendanceUrl: this.attendanceUrlValue,
      viewsUrl: this.viewsUrlValue,
      attendanceAlreadyCompleted: this.attendanceAlreadyCompletedValue,
      attendanceVerificationAudioUrl: this.attendanceVerificationAudioUrlValue
    }, Modal, window.Rollbar && window.Rollbar.captureEvent.bind(window.Rollbar), (e) => { this.showFatalErrorModal(e) }, window.Rollbar)
  }

  initializeLiveWebcast() {
    this.client = createClient() // Remove reference to ZoomMtgEmbedded, TODO: replace when using a new library
    this.meetingSDKElement = document.getElementById(this.containerIdValue)
    this.initClient()
    this.setupScreenSharingCallbacks()
    this.setupConnectionCallbacks()
    this.startMeeting()
  }

  initClient() {
    this.client.init({
      zoomAppRoot: this.meetingSDKElement,
      language: 'en-US',
      customize: {
        meetingInfo: ['topic', 'host', 'participant', 'dc', 'enctype'],
        video: {
          popper: {
            disableDraggable: true
          },
          isResizable: false,
          defaultViewType: "gallery"
        }
      }
    })
  }

  async startMeeting() {
    const response = await AJAX.post(
      this.authenticationEndpointUrlValue,
      { "meeting_number": this.meetingNumberValue }
    )

    const signature = (await response.json()).signature

    this.client.join({
      sdkKey: this.zoomClientIdValue,
      signature: signature,
      meetingNumber: this.meetingNumberValue,
      userName: this.userNameValue,
      userEmail: this.userEmailValue
    }).then(() => {
      this.showJoinComputerAudioTooltip()
      this.attendanceVerification.run(this.client, Modal)
      this.submitCheckpointsToServer()
    })
  }

  setupScreenSharingCallbacks() {
    this.client.on("peer-share-state-change", (e) => {
      switch (e.action) {
        case "Start":
          // The setTimeout avoids a flash of showing the presenter's avatar
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent("webcast.screen_sharing.started"))
            showElement(this.meetingSDKElement)
          }, 2000)

          break
        case "Stop":
          hideElement(this.meetingSDKElement)
          document.dispatchEvent(new CustomEvent("webcast.screen_sharing.stopped"))
          break
      }

      setTimeout(() => { hideElement(document.querySelector("canvas#zoom-sdk-video-canvas").parentElement) }, 1000)
    })
  }

  setupConnectionCallbacks() {
    this.client.on("connection-change", (e) => {
      switch (e.state) {
        case "Connected":
          document.dispatchEvent(new CustomEvent("webcast.started"))
          break
        case "Fail":
          document.dispatchEvent(new CustomEvent("webcast.not_started"))
          break
        case "Closed":
          document.dispatchEvent(new CustomEvent("webcast.ended"))
          break
      }
    })
  }

  submitCheckpointsToServer() {
    return AJAX.post(
      this.attendanceUrlValue,
      { "attendance": { "completed":  false } }
    )
  }

  showJoinComputerAudioTooltip() {
    setTimeout(() => {
      const jau = document.querySelector("button[title='Join Audio']")

      if (jau) {
        jau.id = "zoom_join_audio_button"
        tippy(jau, {
          content: loadTemplate("join_audio_tooltip_template"),
          trigger: "manual",
          allowHTML: true
        })
        const tt = jau._tippy
        tt.show()
      } else  {
        // Report to Rollbar if the button is not found
        if (window.Rollbar) {
          window.Rollbar.error("Join Audio button was not found in Zoom client.")
        }
      }
    }, 3000)
  }

  showFatalErrorModal(e){
    // If the error is related to CSRF we ask the user to sign in again and
    // refresh the page.
    if (e.response.status === 422 && e.response.body.match(/change you wanted was rejected|CSRF/)) {
      Modal.show("sign-in-form-cle-course")
    } else {
      Modal.show("fatal_error_modal")
    }
  }
}
